import type { Department, StructuredProduct } from "@/types/domain";

// El mismo tope que `MAX_TOTAL_TERMS` del backend: cada término es un escaneo.
export const MAX_PRODUCTS = 6;

/** Un chip de arranque: curado a mano, sin IA, y puede traer varios términos. */
export type StarterChip = { label: string; products: StructuredProduct[] };

/** "acero corrugado" → "Acero corrugado": nombre de la línea si la IA no dio uno. */
export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// ---- ZONAS ----
// Los diez que se muestran primero, con su nombre bien escrito: en la BD van en
// mayúsculas y sin tildes ("APURIMAC").
const FEATURED_DEPARTMENTS = [
  "Lima",
  "Cusco",
  "Puno",
  "Arequipa",
  "Piura",
  "Apurímac",
  "Junín",
  "Cajamarca",
  "La Libertad",
  "Áncash",
];

/** Minúsculas y sin tildes, para comparar nombres de la BD con los de pantalla. */
export function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** "LA LIBERTAD" → "La Libertad". Para los departamentos que no están destacados. */
function titleCase(value: string): string {
  return value
    .toLowerCase()
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
}

/** Nombre legible de un departamento: el destacado si lo es, si no en título. */
export function departmentLabel(name: string): string {
  return (
    FEATURED_DEPARTMENTS.find(
      (featured) => normalizeText(featured) === normalizeText(name),
    ) ?? titleCase(name)
  );
}

/** Separa los departamentos en destacados (en su orden) y el resto (alfabético). */
export function splitDepartments(departments: Department[]): {
  featured: Department[];
  others: Department[];
} {
  const featured = FEATURED_DEPARTMENTS.map((name) =>
    departments.find(
      (department) =>
        normalizeText(department.department_name) === normalizeText(name),
    ),
  ).filter((department): department is Department => !!department);
  const featuredIds = new Set(featured.map((d) => d.department_id));
  const others = departments
    .filter((department) => !featuredIds.has(department.department_id))
    .sort((a, b) => a.department_name.localeCompare(b.department_name));
  return { featured, others };
}

/** "Cusco", "Cusco y Puno", "3 departamentos" o "todo el Perú". */
export function zoneLabel(departments: Department[], ids: number[]): string {
  if (ids.length === 0) return "todo el Perú";
  if (ids.length > 2) return `${ids.length} departamentos`;
  return departments
    .filter((department) => ids.includes(department.department_id))
    .map((department) => departmentLabel(department.department_name))
    .join(" y ");
}

// ---- PRODUCTOS ----

/**
 * Suma productos nuevos a los que ya eligió, sin repetir y sin pasar el tope.
 * Las comas se quitan (viajan como separador en la vista previa), pero el espacio
 * del borde se respeta: "pan " no trae PANEL.
 */
export function mergeProducts(
  current: StructuredProduct[],
  incoming: StructuredProduct[],
): StructuredProduct[] {
  const merged = [...current];
  for (const product of incoming) {
    const clean = {
      label: product.label.replace(/,/g, " ").trim(),
      term: product.term.replace(/,/g, " "),
    };
    if (!clean.term.trim()) continue;
    if (merged.some((p) => normalizeText(p.term) === normalizeText(clean.term)))
      continue;
    merged.push(clean);
  }
  return merged.slice(0, MAX_PRODUCTS);
}

/** Lo que vende, para los títulos: "cemento", "cemento y asfalto", "a, b y c". */
export function productHeadline(products: StructuredProduct[]): string {
  const labels = products.map((product) => product.label);
  if (labels.length === 0) return "lo que vendes";
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(", ")} y ${labels.at(-1)}`;
}
